<template>
  <b-container>
    <WizardPageTitle title="Database Models" />
    <b-row>
      <b-col>
        <b-form>
          <b-form-group
            label="Database type"
            description="Database type and document/relation mapper (ORM) to use"
          >
            <b-form-select
              v-model="orm"
              :options="ormChoices"
            />
          </b-form-group>
          <b-form-group
            v-if="orm === 'prisma'"
            label="Prisma database provider"
            description="Database provider to use with Prisma (you can easily change this later)"
          >
            <b-form-select
              v-model="dbProvider"
              :options="providerChoices"
            />
          </b-form-group>
        </b-form>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <h4>Defined database models</h4>
        <b-table
          :items="modelItems"
          :fields="tableFields"
        >
          <template #cell(name)="data">
            <div v-if="data.item === USER_MODEL">
              {{ data.value }}
            </div>
            <div v-else>
              <a
                href="#"
                @click.prevent="openModel(data.item)"
              >{{ data.value }}</a>
            </div>
          </template>
          <template #cell(fields)="data">
            <div v-if="data.value.length">
              {{ idFieldName }},
              {{ data.value.map((f) => f.name).join(', ') }}
            </div>
            <div v-else>
              {{ idFieldName }}
            </div>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="data.item !== USER_MODEL"
              class="text-center"
            >
              <b-icon
                icon="trash"
                variant="danger"
                @click.prevent="remove(data.item)"
              />
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col class="text-right">
        <b-button
          variant="primary"
          @click="createModel"
        >
          <b-icon icon="plus" />
          Add new model
        </b-button>
      </b-col>
    </b-row>
    <WizardPrevNext />
  </b-container>
</template>

<script>
import WizardPageTitle from './widgets/WizardPageTitle.vue';
import WizardPrevNext from './widgets/WizardPrevNext.vue';
import navigationMixin from './mixins/navigation';

// Must match User model defined in the generator backend
const USER_MODEL = {
  name: 'User',
  fields: [
    { name: 'email' },
    { name: 'password' },
    { name: 'token' },
    { name: 'name' },
    { name: 'createdAt' },
    { name: 'lastLoginAt' },
    { name: 'isActive' },
  ],
};

const generateModelName = (modelNames) => {
  for (let seq = modelNames.length + 1; seq < 100; seq += 1) {
    const name = `Model${seq}`;
    if (!modelNames.includes(name)) return name;
  }
  // fallback if we've got over 100 models defined (or more probably, a bug in our code)
  return 'NewModel';
};

export default {
  name: 'Models',
  components: {
    WizardPageTitle,
    WizardPrevNext,
  },
  mixins: [navigationMixin],
  data() {
    return {
      orm: this.$store.state.recipe.orm || 'mongoose',
      dbProvider: this.$store.state.recipe.db_provider || 'sqlite',
      models: this.$store.state.recipe.models || [],
    };
  },
  computed: {
    hasAuth() {
      return !!this.$store.state.recipe.auth;
    },
    ormChoices() {
      return [
        { value: 'mongoose', text: 'MongoDB with Mongoose' },
        { value: 'prisma', text: 'Relational (SQL) with Prisma' },
      ];
    },
    providerChoices() {
      return [
        { value: 'postgresql', text: 'PostgreSQL' },
        { value: 'mysql', text: 'MySQL' },
        { value: 'sqlite', text: 'SQLite' },
        { value: 'sqlserver', text: 'MS SQL Server'}
      ];
    },
    idFieldName() {
      return this.orm === 'prisma' ? 'id' : '_id';
    },
    USER_MODEL: () => USER_MODEL,
    tableFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'fields', label: 'Fields' },
        { key: 'actions', label: '' },
      ];
    },
    modelItems() {
      if (this.hasAuth) {
        return [USER_MODEL, ...this.models];
      }

      return this.models;
    },
  },
  methods: {
    openModel(m) {
      this.$eventHub.$emit('navigate-attempt', `models/${m.name}`);
    },
    createModel() {
      const name = generateModelName(this.models.map((m) => m.name));
      const model = {
        name,
        fields: [],
      };
      this.models.push(model);
      this.$store.commit('updateRecipe', { models: this.models }); // FIXME - store should do this
      this.openModel(model);
    },
    _getRelationSources(model) {
      const sources = [];
      for (const rel of this.$store.getters.activeRelations) {
        if (rel.target === model.name) {
          sources.push(rel.source);
        }
      }
      return sources;
    },
    async remove(model) {
      if (model === USER_MODEL) {
        this.$bvModal.msgBoxOk(
          'User model is required for authentication and can\'t be removed',
        );
        return;
      }

      const sources = this._getRelationSources(model);
      if (sources.length) {
        this.$bvModal.msgBoxOk(
          `Model ${model.name} is referenced by ${sources.join(',')} and can't be removed`,
        );
        return;
      }

      const confirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to remove model "${model.name}"?`,
        {
          okTitle: 'Yes, remove model',
          okVariant: 'danger',
        },
      );
      if (!confirm) return;

      // FIXME - store should do this
      this.models = this.models.filter((m) => m !== model);
      this.$store.commit('updateRecipe', {
        models: this.models,
      });
    },
    commit() {
      this.$store.commit('updateRecipe', {
        orm: this.orm,
        db_provider: this.orm === 'prisma' ? this.dbProvider : null,
      });
      return true;
    },
  },
};
</script>
